import type { JArray, JNumber, JObject, JString } from '../types';

// Create Quote
export interface SpotQuoteCreateValidationSchema extends JObject {
  type: 'object';
  properties: {
    quoteTitle?: JString;
    quoteNumber?: JString;
    cargoType?: JArray;
    consigneeAdditionalInformations: {
      type: 'array';
      items: {
        type: 'object';
        properties: {
          distance: JNumber;
        };
      };
    };
    weightUOM?: JString;
    quoteDetails?: {
      type: 'array';
      items: {
        type: 'object';
        properties: {
          weight?: JNumber;
          weightUom?: JString;
          length?: JNumber;
          width?: JNumber;
          height?: JNumber;
          quantity?: JNumber;
          dimUom?: JString;
          cube?: JNumber;
          class?: JString;
        };
      };
    };
    ami?: JArray;
    costCenter?: JString;
    currencyCode?: JString;
    expDeliveryDate?: JString;
    expShipDate?: JString;
    incotermnamedPlace?: JArray;
    incoterms?: JArray;
    po?: JArray;
    ports?: {
      type: 'array';
      items: {
        type: 'object';
        properties: {
          sequence?: JNumber;
          portType?: JString;
        };
      };
    };
    psl?: JArray;
    quoteKey?: JNumber;
    quoteNo?: JString;
    quoteNotes?: JArray;
    quoteNoNotes?: JArray;
    quoteTerms?: JString;
    shipment?: JString;
    shippers?: {
      type: 'array';
      items: {
        type: 'object';
        properties: {
          sequence?: JNumber;
          stopType?: JString;
          displaySequence?: JNumber;
        };
      };
    };
    shippingRecords?: {
      type: 'array';
      items: {
        type: 'object';
        properties: {
          addr2?: JString;
          city?: JString;
          countryCode?: JString;
          name?: JString;
        };
      };
    };
    trackingDateTime?: JString;
    trailerKey?: JNumber;
    transportationGroup?: JArray;
    transportationMode?: JArray;
    urgency?: JArray;
  };
}

// Edit Quote
export interface SpotQuoteEditValidationSchema extends JObject {
  type: 'object';
  properties: {
    quoteKey: JNumber;
    quoteNo: JString;
    quoteTitle: JString;
    status?: JString;
    currencyCode: JString;
    costCenter?: JString;
    transportationGroup: JString;
    transportationMode: JString;
    shipment?: JString;
    po?: JString;
    ami?: JString;
    expShipDate: JString;
    expDeliveryDate: JString;
    trackingDateTime?: JString;

    ports?: JArray;

    shippingRecords: JArray;
    shippers: JArray;
    consignees: JArray;
    consigneeAdditionalInformations?: JArray;
    trailerKey?: JNumber;

    quoteNotes?: JString;
    quoteTerms?: JString;
    psl?: JString;
    incoterms?: JString;
    urgency: JString;
    incotermnamedPlace?: JString;

    quoteDetails: {
      type: 'array';
      minItems: 1;
      items: {
        type: 'object';
        properties: {
          weight: JNumber;
          weightUom: JString;
          length: JNumber;
          width: JNumber;
          height: JNumber;
          quantity: JNumber;
          dimUom: JString;
          cube: JNumber;
          hazardousClass?: JString;
          harmonizedCode1?: JString;
          harmonizedCode2?: JString;
          productCode?: JString;
          productDescription?: JString;
          class?: JString;
        };
        required: ['weight', 'weightUom', 'length', 'width', 'height', 'quantity', 'dimUom', 'cube'];
      };
    };

    collaborators?: JArray;
  };
  required: [
    'quoteKey',
    'quoteNo',
    'quoteTitle',
    'currencyCode',
    'transportationGroup',
    'transportationMode',
    'expShipDate',
    'expDeliveryDate',
    'shippingRecords',
    'shippers',
    'consignees',
    'urgency',
    'quoteDetails',
  ];
}

// Extract the actual data types from the validation schema
type ExtractType<T> = T extends { type: 'string' }
  ? string
  : T extends { type: 'array' }
    ? any[]
    : T extends { items: { type: 'object'; properties: infer P } }
      ? ExtractObjectType<P>[]
      : T extends { type: 'object'; properties: infer P }
        ? ExtractObjectType<P>
        : T extends object
          ? ExtractObjectType<T>
          : unknown;

type ExtractObjectType<T> = {
  [K in keyof T]: ExtractType<T[K]>;
};

// Create the Fields type that matches the validation schema structure
export type _Fields = {
  [K in keyof SpotQuoteCreateValidationSchema['properties']]: ExtractType<
    SpotQuoteCreateValidationSchema['properties'][K]
  >;
};

export type FieldsForSpotQuoteCreate = keyof SpotQuoteCreateValidationSchema['properties'];

export type FieldsForSpotQuoteEdit = keyof SpotQuoteEditValidationSchema['properties'];

export type SpotQuoteFields = FieldsForSpotQuoteCreate | FieldsForSpotQuoteEdit;

export const SPOT_QUOTE_LABELS: {
  [Key in SpotQuoteFields]?: string | Record<string, string>;
} = {
  ami: 'Billing Reference#',
  cargoType: 'Cargo Type',
  consigneeAdditionalInformations: {
    distance: 'Distance',
  },
  costCenter: 'Cost Center',
  currencyCode: 'Currency',
  expDeliveryDate: 'Delivery Date',
  expShipDate: 'Ship Date',
  incotermnamedPlace: 'Incoterm Named Place',
  incoterms: 'Incoterms',
  po: 'PO#',
  ports: {
    addr1: 'Address',
    addr2: 'Address 2',
    city: 'City',
    countryCode: 'Country',
    name: 'Port Name',
    portType: 'Port Type',
    sequence: 'Stop No',
    state: 'State',
    stopType: 'Stop Type',
    zip: 'Port Code',
  },
  psl: 'PSL',
  quoteDetails: {
    class: 'Rating Class',
    cube: 'Cube',
    dimUom: 'Dim Uom',
    harmonizedCode1: 'Harmonized Code - 1',
    harmonizedCode2: 'Harmonized Code - 2',
    hazardousClass: 'Hazardous Class',
    height: 'Height',
    length: 'Length',
    productCode: 'Product Code',
    productDescription: 'Product Description',
    quantity: 'Quantity',
    weight: 'Weight',
    weightUom: 'Weight Uom',
    width: 'Width',
  },
  quoteNo: 'Quote No',
  quoteNotes: 'Quote Notes',
  quoteNumber: 'Quote Number',
  quoteTerms: 'Shipment Term',
  quoteTitle: 'Quote Title',
  shipment: 'Shipment#',
  shippers: {
    stopType: 'Stop Type',
  },
  shippingRecords: {
    addr2: 'Address 2',
    city: 'City',
    countryCode: 'Country',
    name: 'Company Name',
  },
  status: 'Status',
  trackingDateTime: 'Bid Expiration',
  trailerKey: 'Equipment Type',
  transportationGroup: 'Transportation Group',
  transportationMode: 'Transportation Mode',
  urgency: 'Shipment Type',
} as const;
